import React from "react";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CALCULATION_OPS } from "../../../lib/bands/bandModel";
import { NumberValue } from "./VisibilitySettings";

// Calculations (docs/bands-spec.md §8.3b): an element that prints the sum of
// other elements in its section and fixed numbers, e.g. Net Total + Delivery
// Charges.

// "Calculations" in a section's panel. `selectedCount` is how many elements
// of this section are selected; they become the calculation's first terms.
export function CalculationTool({ selectedCount, onAdd }) {
  return (
    <div className="calculation-tool border-bottom pb-3 mb-3">
      <Form.Label className="fw-bold mb-1">Calculations</Form.Label>
      <Form.Text className="text-muted d-block mb-2">
        Adds a value that adds up or subtracts other values in this section, e.g. Net Total + Delivery Charges.
        {selectedCount > 1 ? "" : " Tip: Ctrl+click the values on the page first to start with them."}
      </Form.Text>
      <button type="button" className="btn btn-sm btn-outline-primary" onClick={onAdd}>
        <FontAwesomeIcon icon="calculator" className="me-1" />
        {selectedCount > 1 ? `Add up the ${selectedCount} selected values` : "Add a calculation"}
      </button>
    </div>
  );
}

const NUMBER = "number";

// The terms of one calculation, in the properties panel.
//   terms    [{ op, element } | { op, value }]
//   options  the elements it can use: [{ id, label }]
//   labelOf  (id) => label, also for elements no longer in `options`
export function CalculationEditor({ terms, options, labelOf, onChange }) {
  const list = terms || [];
  const set = (index, term) => onChange(list.map((t, i) => (i === index ? term : t)));
  const remove = (index) => onChange(list.filter((_, i) => i !== index));
  const add = () => {
    const used = new Set(list.map((t) => t.element));
    const next = options.find((o) => !used.has(o.id));
    onChange([...list, next ? { op: CALCULATION_OPS.ADD, element: next.id } : { op: CALCULATION_OPS.ADD, value: 0 }]);
  };

  return (
    <div className="calculation-editor">
      {list.length === 0 && (
        <div className="text-muted small mb-2">Nothing added yet. Add the values to add up or subtract.</div>
      )}
      {list.map((term, index) => {
        const isNumber = term.element === undefined;
        const missing = !isNumber && !options.some((o) => o.id === term.element);
        const subtract = term.op === CALCULATION_OPS.SUBTRACT;
        return (
          <div className="calculation-term" key={index}>
            <button
              type="button"
              className={"calculation-op" + (subtract ? " is-subtract" : "")}
              title={subtract ? "Subtracted: click to add instead" : "Added: click to subtract instead"}
              aria-label={subtract ? "Subtract" : "Add"}
              onClick={() => set(index, { ...term, op: subtract ? CALCULATION_OPS.ADD : CALCULATION_OPS.SUBTRACT })}
            >
              {subtract ? "−" : "+"}
            </button>
            <select
              className="form-select form-select-sm"
              value={isNumber ? NUMBER : term.element}
              onChange={(e) => set(index, e.target.value === NUMBER
                ? { op: term.op, value: 0 }
                : { op: term.op, element: e.target.value })}
            >
              {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              {missing && <option value={term.element}>{labelOf(term.element)}</option>}
              <option value={NUMBER}>Number</option>
            </select>
            {isNumber && (
              <div className="calculation-number">
                <NumberValue value={Number(term.value) || 0} onChange={(value) => set(index, { op: term.op, value })} />
              </div>
            )}
            <button type="button" className="calculation-remove" title="Remove" aria-label="Remove" onClick={() => remove(index)}>
              <FontAwesomeIcon icon="times" />
            </button>
          </div>
        );
      })}
      <button type="button" className="btn btn-sm btn-outline-secondary mt-1" onClick={add}>
        <FontAwesomeIcon icon="plus" className="me-1" />
        Add a value
      </button>
    </div>
  );
}

// A calculation's short name: "Calculation", or "Calculation 2" for the
// second one added to a section (its name ends in "-2").
export function calculationName(element) {
  const number = (String(element?.name || "").match(/^System\.Calculation-(\d+)$/) || [])[1];
  return number ? `Calculation ${number}` : "Calculation";
}

// "Net Total + Delivery Charges − 5", for the tooltip and the panel.
export function describeTerms(terms, labelOf) {
  return (terms || [])
    .map((term, i) => {
      const name = term.element === undefined ? String(Number(term.value) || 0) : labelOf(term.element);
      const sign = term.op === CALCULATION_OPS.SUBTRACT ? "− " : i > 0 ? "+ " : "";
      return sign + name;
    })
    .join(" ");
}
